import type { JsonObject } from '@fabric-harness/sdk';
import type {
  ColumnInfo,
  StatementExecutionClient,
  StatementResponse,
} from '@databricks/sdk-statementexecution/v1';

export type DatabricksStatementClient = Pick<
  StatementExecutionClient,
  'executeStatement' | 'getStatementResult'
>;

export interface SqlParameter {
  name: string;
  value: string;
  /** SQL type for the named marker (e.g. 'DATE'). Defaults to STRING on the Databricks side. */
  type?: string;
}

export interface StatementPoll {
  intervalMs: number;
  timeoutMs: number;
}

const TERMINAL_STATES = new Set(['SUCCEEDED', 'FAILED', 'CANCELED', 'CLOSED']);

/** Submit a parameterized SQL statement and poll to a terminal state. Named parameters → injection-safe. */
export async function runStatement(
  client: DatabricksStatementClient,
  warehouseId: string,
  statement: string,
  parameters: SqlParameter[],
  poll: StatementPoll,
): Promise<unknown> {
  const submit = await client.executeStatement({
    warehouseId,
    statement,
    waitTimeout: '30s',
    disposition: 'INLINE',
    ...(parameters.length > 0 ? { parameters } : {}),
  });
  if (TERMINAL_STATES.has(submit.status?.state ?? '')) return submit;
  const id = submit.statementId;
  if (!id) return submit;

  const deadline = Date.now() + poll.timeoutMs;
  let current: StatementResponse = submit;
  while (!TERMINAL_STATES.has(current.status?.state ?? '') && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, poll.intervalMs));
    current = await client.getStatementResult({ statementId: id });
  }
  return current;
}

/** Map a statement response's columns + rows into objects keyed by column name. */
export function parseStatementRows(response: unknown): JsonObject[] {
  const res = response as StatementResponse;
  const columns = res.manifest?.schema?.columns ?? [];
  const names = columns.map((c: ColumnInfo) => c.name ?? '');
  const rows = (res.result?.dataArray ?? []) as unknown[][];
  return rows.map((row: unknown[]) => {
    const record: JsonObject = {};
    names.forEach((name: string, i: number) => { record[name] = row[i] as JsonObject[string]; });
    return record;
  });
}
